import React, { useState, useEffect } from 'react'
import axiosInstance from '../../services/axiosInstance'
import PageHeader from '../../components/common/PageHeader'
import {
  Plane, Mail, FileText, Calendar, ShieldCheck,
  CheckCircle2, Circle, HelpCircle, MapPin, ExternalLink, Car, Home, X
} from 'lucide-react'
import Autocomplete from 'react-google-autocomplete'
import StudentVisaModals from '../../components/modals/StudentVisaModals'
import './styles.scss'

export default function StudentVisa() {
  const [loading, setLoading] = useState(true)
  const [applicationStatus, setApplicationStatus] = useState('DRAFT')
  const [i20Profile, setI20Profile] = useState(null)

  // Modal states
  const [activeModal, setActiveModal] = useState(null)
  const [addressInput, setAddressInput] = useState('')
  const [flightInput, setFlightInput] = useState({ number: '', airport: '', dest: '', time: '' })

  useEffect(() => {
    fetchVisaData()
  }, [])

  const fetchVisaData = async () => {
    try {
      setLoading(true)
      const res = await axiosInstance.get('/api/i20-visa')
      const data = res.data?.data
      if (data) {
        setApplicationStatus(data.applicationStatus)
        setI20Profile(data.i20Profile)
        setAddressInput(data.i20Profile?.mailingAddress || '')
        setFlightInput({
          number: data.i20Profile?.flightNumber || '',
          airport: data.i20Profile?.arrivalAirport || '',
          dest: data.i20Profile?.destination || '',
          time: data.i20Profile?.flightTime || ''
        })
      }
    } catch (e) {
      console.error('Failed to load visa data', e)
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (updates) => {
    const backup = { ...i20Profile }
    setI20Profile(prev => ({ ...prev, ...updates }))
    setActiveModal(null)

    try {
      await axiosInstance.put('/api/i20-visa', updates)
    } catch (e) {
      console.error('Update failed', e)
      setI20Profile(backup)
      alert('Failed to update status. Please try again.')
    }
  }

  if (loading) {
    return (
      <div className="page-container">
        <PageHeader title="I-20 & Visa Tracker" subtitle="Track your path to the US" />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <div className="spinner" style={{ width: '40px', height: '40px', border: '3px solid rgba(37,99,235,0.2)', borderTopColor: '#2563EB', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    )
  }

  if (applicationStatus !== 'APPROVED' && applicationStatus !== 'ENROLLED' && applicationStatus !== 'ARRIVAL_READY') {
    return (
      <div className="page-container student-visa">
        <PageHeader title="I-20 & Visa Tracker" />
        <div className="pre-approval-state">
          <div className="icon-circle">
            <Plane size={40} color="#1d4ed8" />
          </div>
          <h2>Your Journey to the US</h2>
          <p className="subtitle">The I-20 and Visa process will unlock automatically as soon as your university approves your application documents!</p>

          <div className="info-card">
            <h3>What happens next?</h3>
            <div className="step-row">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Document Approval</h4>
                <p>The admissions team verifies your passport and financial proofs.</p>
              </div>
            </div>
            <div className="step-row">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>I-20 Issuance & Shipping</h4>
                <p>The university issues your I-20 form and mails it to your address.</p>
              </div>
            </div>
            <div className="step-row">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>SEVIS & Visa Interview</h4>
                <p>You pay the SEVIS fee, submit the DS-160, and schedule your interview at the US Embassy.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!i20Profile) return null

  return (
    <div className="page-container student-visa">
      <PageHeader
        title="I-20 & Visa Tracker"
        subtitle="Manage your I-20 shipping and F-1 Visa requirements."
      />

      <div className="tracker-grid">
        {/* I-20 Shipping Card */}
        <div className="tracking-card interactive" onClick={() => { setAddressInput(i20Profile.mailingAddress || ''); setActiveModal('ADDRESS') }}>
          <div className="card-header">
            <Mail size={24} color="#1d4ed8" />
            <h3>I-20 Package Status</h3>
          </div>
          <div className="status-badge">
            {i20Profile.i20Status?.replace('_', ' ') || 'PROCESSING'}
          </div>

          <div className="shipping-details">
            <span className="label">Mailing Address</span>
            <span className="value">{i20Profile.mailingAddress || 'Tap to provide your mailing address'}</span>
          </div>

          <div className="address-warning">
            <HelpCircle size={16} color="#d97706" />
            <span>Physical delivery requires a highly accurate address. Click to verify or update your destination.</span>
          </div>

          {i20Profile.trackingNumber && (
            <div className="shipping-details">
              <span className="label">Tracking Number ({i20Profile.shippingCarrier})</span>
              <span className="value">{i20Profile.trackingNumber}</span>
            </div>
          )}
        </div>

        {/* Visa Checklist */}
        <div className="tracking-card">
          <div className="card-header">
            <FileText size={24} color="#1d4ed8" />
            <h3>Visa Requirements</h3>
          </div>

          <div className="checklist-row" onClick={() => setActiveModal('SEVIS')}>
            {i20Profile.sevisStatus === 'PAID' ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
            <div className="content">
              <h4>Pay SEVIS I-901 Fee</h4>
              <p>{i20Profile.sevisStatus === 'PAID' ? 'Completed' : 'A mandatory $350 fee paid to the US Govt to activate your I-20.'}</p>
            </div>
          </div>

          <div
            className={`checklist-row ${i20Profile.sevisStatus !== 'PAID' ? 'disabled' : ''}`}
            onClick={() => i20Profile.sevisStatus === 'PAID' && setActiveModal('DS160')}
          >
            {i20Profile.ds160Status === 'SUBMITTED' ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
            <div className="content">
              <h4>Submit DS-160 Form</h4>
              <p>{i20Profile.ds160Status === 'SUBMITTED' ? 'Completed' : 'The official online US Visa application form.'}</p>
            </div>
          </div>

          <div
            className={`checklist-row ${i20Profile.ds160Status !== 'SUBMITTED' ? 'disabled' : ''}`}
            onClick={() => i20Profile.ds160Status === 'SUBMITTED' && setActiveModal('INTERVIEW')}
          >
            {i20Profile.visaAppointmentStatus === 'SCHEDULED' ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
            <div className="content">
              <h4>Schedule Embassy Interview</h4>
              <p>{i20Profile.visaAppointmentStatus === 'SCHEDULED' ? 'Scheduled' : 'In-person appointment at your local US Embassy.'}</p>
            </div>
          </div>

          <div
            className={`checklist-row no-border ${i20Profile.visaAppointmentStatus !== 'SCHEDULED' ? 'disabled' : ''}`}
            onClick={() => i20Profile.visaAppointmentStatus === 'SCHEDULED' && setActiveModal('VISA_DECISION')}
          >
            {i20Profile.visaDecisionStatus === 'APPROVED' ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
            <div className="content">
              <h4>Visa Interview Outcome</h4>
              <p>{i20Profile.visaDecisionStatus === 'APPROVED' ? 'Approved' : 'Update your status after the interview.'}</p>
            </div>
          </div>
        </div>

        {/* Pre-Departure section appears once Visa is Approved */}
        {i20Profile.visaDecisionStatus === 'APPROVED' && (
          <div className="tracking-card">
            <div className="card-header">
              <Plane size={24} color="#1d4ed8" />
              <h3>Arrival & Housing Checklist</h3>
            </div>

            {/* Flight Details */}
            <div className="checklist-row" onClick={() => setActiveModal('FLIGHT')}>
              {i20Profile.flightTicketStatus === 'BOOKED' ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
              <div className="content">
                <h4>Book Flight Tickets</h4>
                <p>{i20Profile.flightTicketStatus === 'BOOKED' ? 'Flight Details Saved' : 'Purchase your tickets to the US.'}</p>
              </div>
            </div>

            {i20Profile.flightTicketStatus === 'BOOKED' && (
              <div className="expanded-info">
                <div className="info-box" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', marginBottom: '0.5rem' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    <div>
                      <span className="label" style={{ fontSize: '0.8rem', color: '#64748b', display: 'block' }}>Flight</span>
                      <span className="value" style={{ fontWeight: '500' }}>{i20Profile.flightNumber || 'Not provided'}</span>
                    </div>
                    <div>
                      <span className="label" style={{ fontSize: '0.8rem', color: '#64748b', display: 'block' }}>Arrival</span>
                      <span className="value" style={{ fontWeight: '500' }}>{i20Profile.arrivalAirport || 'Unknown'} • {i20Profile.flightTime || 'Unknown'}</span>
                    </div>
                    <div>
                      <span className="label" style={{ fontSize: '0.8rem', color: '#64748b', display: 'block' }}>Destination</span>
                      <span className="value" style={{ fontWeight: '500' }}>{i20Profile.destination || 'Not provided'}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Airport Pickup */}
            <div className="checklist-row" onClick={() => setActiveModal('PICKUP')}>
              {['REQUESTED', 'ARRANGED'].includes(i20Profile.airportPickupRequested) ? <CheckCircle2 size={24} color="#10b981" /> : <Circle size={24} color="#cbd5e1" />}
              <div className="content">
                <h4>Arrange Airport Pickup</h4>
                <p>{i20Profile.airportPickupRequested === 'ARRANGED' ? 'Pickup Arranged' : i20Profile.airportPickupRequested === 'REQUESTED' ? 'Pickup Requested' : 'Do you need a ride from the airport?'}</p>
              </div>
            </div>

            {i20Profile.airportPickupRequested === 'REQUESTED' && (
              <div className="expanded-info">
                <div className="alert-box success" style={{ display: 'flex', gap: '0.75rem', background: '#ecfdf5', padding: '1rem', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                  <Car size={20} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#065f46', fontSize: '0.95rem' }}>Request Received</h5>
                    <p style={{ margin: 0, color: '#047857', fontSize: '0.85rem', lineHeight: '1.4' }}>Our team will review your flight details and contact you via email with your pickup assignment 48 hours prior to arrival.</p>
                  </div>
                </div>
              </div>
            )}

            {i20Profile.airportPickupRequested === 'ARRANGED' && (
              <div className="expanded-info">
                <div className="alert-box success" style={{ display: 'flex', gap: '0.75rem', background: '#ecfdf5', padding: '1rem', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                  <Car size={20} color="#059669" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#065f46', fontSize: '0.95rem' }}>Pickup Arranged</h5>
                    <p style={{ margin: 0, color: '#047857', fontSize: '0.85rem', lineHeight: '1.4' }}>Your airport pickup has been successfully scheduled. Check your email for driver details.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Accommodation */}
            <div className="checklist-row no-border" onClick={() => setActiveModal('ACCOMMODATION')}>
              {i20Profile.accommodationStatus === 'SECURED' ? (
                <CheckCircle2 size={24} color="#10b981" />
              ) : i20Profile.accommodationStatus === 'NEEDS_HELP' ? (
                <HelpCircle size={24} color="#f59e0b" />
              ) : (
                <Circle size={24} color="#cbd5e1" />
              )}
              <div className="content">
                <h4>Secure Accommodation</h4>
                <p>
                  {i20Profile.accommodationStatus === 'SECURED' ? 'Housing Secured' 
                   : i20Profile.accommodationStatus === 'NEEDS_HELP' ? 'Housing Assistance Requested' 
                   : 'Book your on-campus or off-campus housing.'}
                </p>
              </div>
            </div>

            {i20Profile.accommodationStatus === 'NEEDS_HELP' && (
              <div className="expanded-info">
                <div className="alert-box warning" style={{ display: 'flex', gap: '0.75rem', background: '#fffbeb', padding: '1rem', borderRadius: '8px', border: '1px solid #fde68a', marginTop: '0.5rem' }}>
                  <Home size={20} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <h5 style={{ margin: '0 0 0.25rem 0', color: '#92400e', fontSize: '0.95rem' }}>Assistance Requested</h5>
                    <p style={{ margin: 0, color: '#b45309', fontSize: '0.85rem', lineHeight: '1.4' }}>Our Housing Office has been notified. They will email you a list of available on-campus dorms and verified apartments soon.</p>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {/* Modals Component */}
      <StudentVisaModals
        activeModal={activeModal}
        setActiveModal={setActiveModal}
        i20Profile={i20Profile}
        updateProfile={updateProfile}
        addressInput={addressInput}
        setAddressInput={setAddressInput}
        flightInput={flightInput}
        setFlightInput={setFlightInput}
      />
    </div>
  )
}
